import React, { useMemo, useState } from 'react';
import { ExtraTableRenderSettings } from '../../components/ExtraFields';
import { AbsenceRequest } from './types';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { useObjectLabels } from '../../hooks/useObjectLabels';
import { ExtraFieldsTableCustomColumns } from '../../components/ExtraFields/types';
import { EmployeeWithAvatar } from '../../components/Employee';
import AbsenceDateTimeRange from '../../pages/Absences/components/AbsenceDateTimeRange';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';
import AbsenceTypeCell from '../../pages/Absences/components/AbsenceTypeCell';
import { useAbsencesTableRenderSettings } from '../../pages/Absences/useAbsencesTableRenderSettings';
import AbsenceStatus from '../../pages/Absences/components/AbsenceStatus';
import FormattedDateTime from '../../../../components/datetime/FormattedDateTime';
import MultiDayAbsenceBtn from '../../pages/Absences/components/MultiDayAbsenceDetails/MultiDayAbsenceBtn';
import { isMultiDayAndHourly } from '../../pages/Absences/components/MultiDayAbsenceDetails/logic';

type Props = {
  onLoomVideoClick: (loomVideo: AbsenceRequest['loomVideo']) => void;
  onAbsenceClick?: (absence: AbsenceRequest) => void;
};

const i18Path = 'absences.myAbsences.table';

export const useAbsenceRequestTableRenderSettings = ({
  onLoomVideoClick,
  onAbsenceClick,
}: Props) => {
  const t = useNamespacedTranslation(i18Path);
  const objectLabels = useObjectLabels();
  const [
    selectedAbsenceIdForMultiDayAbsence,
    setSelectedAbsenceIdForMultiDayAbsence,
  ] = useState<string>('');

  const handleCloseSidebar = () => {
    setSelectedAbsenceIdForMultiDayAbsence('');
  };

  const { columns: absenceColumns } =
    useAbsencesTableRenderSettings<AbsenceRequest>({
      onLoomVideoClick,
      onMultiDayAbsenceClick: setSelectedAbsenceIdForMultiDayAbsence,
    });

  const columns: ExtraTableRenderSettings<AbsenceRequest>['columns'] =
    useMemo(() => {
      return {
        ...absenceColumns,
        [ExtraFieldsTableCustomColumns.absences.employee]: {
          Header: objectLabels.employee,
          Cell: (props) => (
            <div>
              <EmployeeWithAvatar employee={props.row.original.requester} />
            </div>
          ),
        },
        [ExtraFieldsTableCustomColumns.absences.name]: {
          Header: t('duration'),
          accessor: 'startDate', // for sorting
          Cell: ({ row }) => {
            return (
              <>
                <div
                  className="link-primary cu-pointer"
                  onClick={() => onAbsenceClick?.(row.original)}>
                  <AbsenceDateTimeRange absence={row.original} />
                  {row.original.comment && (
                    <OverlayTrigger
                      overlay={
                        <Tooltip id={`tooltip`}>{row.original.comment}</Tooltip>
                      }>
                      <span>
                        <FlairIcon
                          role="button"
                          className={'ms-2 cursor-auto'}
                          icon="chatbubble-outline"
                        />
                      </span>
                    </OverlayTrigger>
                  )}
                  {row.original.attachments.length > 0 && (
                    <span>
                      <FlairIcon
                        role="button"
                        className={'ms-2'}
                        icon="document-outline"
                      />
                    </span>
                  )}
                </div>
                {isMultiDayAndHourly(row.original) && (
                  <>
                    &nbsp;&#xB7;&nbsp;
                    <MultiDayAbsenceBtn
                      absenceId={row.original.absenceId}
                      onClick={setSelectedAbsenceIdForMultiDayAbsence}
                    />
                  </>
                )}
              </>
            );
          },
        },
        [ExtraFieldsTableCustomColumns.absences.absenceCategory]: {
          Header: t('type'),
          accessor: 'categoryName',
          Cell: ({ row: { original } }) => <AbsenceTypeCell {...original} />,
        },
        flair__Approval_Status__c: {
          Header: t('status'),
          Cell: ({ row }) => {
            return (
              <AbsenceStatus status={row.original.absenceApprovalStatus} />
            );
          },
        },
        CreatedDate: {
          Header: t('requested'),
          Cell: ({ value }) => {
            return <FormattedDateTime dateTime={value} format="long" />;
          },
        },
      };
    }, [
      t,
      objectLabels,
      absenceColumns,
      setSelectedAbsenceIdForMultiDayAbsence,
      onAbsenceClick,
    ]);

  return {
    columns,
    selectedAbsenceIdForMultiDayAbsence,
    handleCloseSidebar,
  };
};
